import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { fmtQty, lineGap, lineTotals } from "./mcfUtils";


function SkuCell({ item, idx, productOptions, products, update }) {
  if (item.matched !== false) {
    return (
      <div>
        <p className="font-mono text-xs">{item.sku_id}</p>
        <p className="text-sm">{item.sku_name}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1 min-w-[220px]">
      <p className="text-xs text-amber-800 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Not matched: “{item.invoice_sku_name || item.item_code || "unknown"}”</p>
      <SearchableSelect
        options={productOptions}
        value={item.sku_id}
        onValueChange={(v) => {
          const p = products.find((x) => x.sku_id === v);
          update(idx, { sku_id: p.sku_id, sku_name: p.product_name, item_code: p.item_code || item.item_code, matched: true });
        }}
        placeholder="Select SKU from Products"
        data-testid={`mcf-item-sku-select-${idx}`}
      />
      <div className="flex gap-1">
        <Input className="h-7 text-xs" placeholder="or SKU ID" value={item.sku_id} onChange={(e) => update(idx, { sku_id: e.target.value })} data-testid={`mcf-item-sku-id-${idx}`} />
        <Input className="h-7 text-xs" placeholder="SKU Name" value={item.sku_name} onChange={(e) => update(idx, { sku_name: e.target.value })} data-testid={`mcf-item-sku-name-${idx}`} />
      </div>
    </div>
  );
}

export function TransferItemsTable({ items, setItems, products, isAdmin }) {
  const productOptions = useMemo(
    () => products.map((p) => ({ value: p.sku_id, label: `${p.product_name} (${p.sku_id})`, searchTerms: [p.item_code, p.sku_id] })),
    [products]
  );
  const update = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const add = () => setItems((arr) => [...arr, { sku_id: "", sku_name: "", item_code: "", sent_qty: "", grn_qty: 0, matched: false }]);
  const t = lineTotals(items);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>SKU ID / Name</TableHead>
              <TableHead>Item Code</TableHead>
              <TableHead className="text-right w-[110px]">Sent Qty</TableHead>
              <TableHead className="text-right w-[110px]">GRN Qty</TableHead>
              <TableHead className="text-right w-[100px]">Pending GRN</TableHead>
              <TableHead className="text-right w-[100px]">Excess GRN</TableHead>
              <TableHead className="w-[40px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">No SKU lines. Add lines manually.</TableCell></TableRow>
            )}
            {items.map((it, idx) => {
              const sent = Number(it.sent_qty) || 0;
              const grn = Number(it.grn_qty) || 0;
              const excess = grn > sent;
              return (
                <TableRow key={idx} data-testid={`mcf-item-row-${idx}`} className={it.matched === false ? "bg-amber-50/50" : ""}>
                  <TableCell><SkuCell item={it} idx={idx} productOptions={productOptions} products={products} update={update} /></TableCell>
                  <TableCell><Input className="h-8 w-[110px] font-mono text-xs" value={it.item_code || ""} onChange={(e) => update(idx, { item_code: e.target.value })} data-testid={`mcf-item-code-${idx}`} /></TableCell>
                  <TableCell><Input type="number" min="0" className={`h-8 text-right ${!sent ? "border-rose-400" : ""}`} value={it.sent_qty ?? ""} onChange={(e) => update(idx, { sent_qty: e.target.value })} data-testid={`mcf-item-sent-${idx}`} /></TableCell>
                  <TableCell>
                    <Input type="number" min="0" disabled={!isAdmin} className={`h-8 text-right ${excess ? "border-rose-400" : ""}`} value={it.grn_qty ?? ""} onChange={(e) => update(idx, { grn_qty: e.target.value })} data-testid={`mcf-item-grn-${idx}`} />
                    {excess && (
                      isAdmin ? (
                        <label className="flex items-center gap-1 mt-1 text-[11px] text-rose-700">
                          <Checkbox checked={!!it.discrepancy_approved} onCheckedChange={(v) => update(idx, { discrepancy_approved: !!v })} data-testid={`mcf-item-discrepancy-${idx}`} />
                          Approve excess
                        </label>
                      ) : <p className="text-[11px] text-rose-700 mt-1">Exceeds sent (admin only)</p>
                    )}
                  </TableCell>
                  <TableCell className={`text-right font-medium ${lineGap(it).pending ? "text-amber-700" : "text-emerald-700"}`} data-testid={`mcf-item-pending-${idx}`}>{fmtQty(lineGap(it).pending)}</TableCell>
                  <TableCell className={`text-right font-medium ${lineGap(it).excess ? "text-rose-700" : "text-slate-400"}`} data-testid={`mcf-item-excess-${idx}`}>{fmtQty(lineGap(it).excess)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => remove(idx)} data-testid={`mcf-item-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length > 0 && (
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell className="text-right" data-testid="mcf-items-total-sent">{fmtQty(t.sent)}</TableCell>
                <TableCell className="text-right" data-testid="mcf-items-total-grn">{fmtQty(t.grn)}</TableCell>
                <TableCell className="text-right text-amber-700" data-testid="mcf-items-total-pending">{fmtQty(t.pending)}</TableCell>
                <TableCell className="text-right text-rose-700" data-testid="mcf-items-total-excess">{fmtQty(t.excess)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Button size="sm" variant="outline" onClick={add} data-testid="mcf-item-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add SKU line</Button>
    </div>
  );
}
